import { CommonContextProvider, msalInstance } from '@abdoo/common';
import {
  AuthenticatedTemplate,
  MsalProvider,
  UnauthenticatedTemplate,
} from '@azure/msal-react';
import { AppInsightsContext, ReactPlugin } from '@microsoft/applicationinsights-react-js';
import {
  ApplicationInsights,
  ITelemetryPlugin,
} from '@microsoft/applicationinsights-web';
import { Box } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserHistory } from 'history';
import React, { createContext, useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router } from 'react-router-dom';

import { environment } from '../environment';
import { NonAuthedRoutes } from '../NonAuthedRoutes';
import { Routes } from '../Routes';
import { UserDataWrapper } from '../user/UserDataWrapper';

const browserHistory = createBrowserHistory({ basename: '' });
const reactPlugin = new ReactPlugin();

const appInsights = new ApplicationInsights({
  config: {
    instrumentationKey: environment.instrumentationKey,
    // Type-hacking due to https://github.com/microsoft/applicationinsights-react-js/issues/32
    extensions: [reactPlugin as unknown as ITelemetryPlugin],
    extensionConfig: {
      [reactPlugin.identifier]: { history: browserHistory },
    },
  },
});
appInsights.loadAppInsights();

interface AppContextModel {
  pageTitle: string;
  setPageTitle: React.Dispatch<React.SetStateAction<string>>;
}

const defaultState = {
  pageTitle: '',
  setPageTitle: null as unknown as React.Dispatch<React.SetStateAction<string>>,
} as AppContextModel;

export const AppContext: React.Context<AppContextModel> = createContext(defaultState);
const queryClient = new QueryClient();

export const App = () => {
  const [pageTitle, setPageTitle] = useState('employee');

  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <HelmetProvider>
            <Helmet>
              <title>Abdoo: {pageTitle}</title>
            </Helmet>
            <CommonContextProvider>
              <AppContext.Provider
                value={{
                  pageTitle,
                  setPageTitle,
                }}
              >
                <Router>
                  <Box sx={{ backgroundColor: 'white', color: 'black' }}>
                    <AuthenticatedTemplate>
                      <UserDataWrapper>
                        <Routes />
                      </UserDataWrapper>
                    </AuthenticatedTemplate>
                    <UnauthenticatedTemplate>
                      <NonAuthedRoutes />
                    </UnauthenticatedTemplate>
                  </Box>
                </Router>
              </AppContext.Provider>
            </CommonContextProvider>
          </HelmetProvider>
        </QueryClientProvider>
      </MsalProvider>
    </AppInsightsContext.Provider>
  );
};
